import "./style.scss";

import { useAuthenticator } from "@aws-amplify/ui-react";
import { TabContext, TabPanel } from "@mui/lab";
import AccountingSettings from "components/AccountingSettings";
import AdvanceCustomStatusSettings from "components/AdvanceCustomStatusSettings";
import AdvanceProvisionSettings from "components/AdvanceProvisionSettings";
import AnalyticalCode from "components/AnalyticalCode";
import CalculationBaseColumnLabels from "components/CalculationBaseColumnLabels";
import APIKeysSettings from "components/APIKeysSettings";
import CompanyInfo from "components/CompanyInfo";
import ContractsSettings from "components/ContractsSettings";
import CurrencySettings from "components/CurrencySettings";
import DocoonSettings from "components/DocoonSettings";
import EDIInterforumSettings from "components/EDIInterforumSettings";
import ExportSettings from "components/ExportSettings";
import History from "components/History";
import IntegrationsSettings from "components/IntegrationsSettings";
import MemberList from "components/MemberList";
import MultipleBrandSettings from "components/MultipleBrandSettings";
import PartnerStatementsSettings from "components/PartnerStatementsSettings";
import RolesSettings from "components/RolesSettings";
import ScopedNotificationSettings from "components/ScopedNotificationSettings";
import AccountEmail from "components/shared/AccountEmail";
import AccountSettings from "components/shared/AccountSettings";
import NavigationBarSettings from "components/shared/NavigationBarSettings";
import NotificationSettings from "components/shared/NotificationSettings";
import StatementSettings from "components/shared/StatementSettings";
import URSSAFCompanySettings from "components/shared/URSSAFCompanySettings";
import TermTemplates from "components/TermTemplates";
import Channels from "containers/Channels";
import FormatTypesSettings from "components/FormatTypesSettings";
import CustomFieldsContainer from "containers/CustomFieldsContainer";
import { metadataResourceForAccess, metadataResourceFromPath } from "containers/CustomFieldsContainer/metadataTabs";
import BeneficiaryDocumentsSettings from "components/BeneficiaryDocumentsSettings";
import InvoicePercentages from "containers/InvoicePercentages";
import ServicesPage from "containers/ServicesPage";
import { PortalType } from "contexts/portal.context";
import { getCognitoUserAttribute } from "helpers/utils";
import { useAbility } from "hooks/useAbility";
import { useCompanySettings } from "hooks/useCompanySettings";
import usePageTitle from "hooks/usePageTitle";
import useUserRole from "hooks/useUserRole";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { IntegrationStatus } from "store/integrationsStore";
import type { RootState } from "store/store";
import { useAppSelector } from "store/store";
import { EPortalType } from "store/types";

import type { TSettings } from "./types";

export interface IExtraConditions {
    usesBrands: boolean;
    usesAgreements: boolean;
    usesMultiCurrency: boolean;
    usesStatementTemplates: boolean;
    usesMetadata: boolean;
    usesBexio: boolean;
    usesIRCEC: boolean;
    usesScopedNotifications: boolean;
    usesInterforumEDI: boolean;
    usesCustomRoles: boolean;
    hasDocoonIntegration: boolean;
    canReadAdvanceProvision: boolean;
    canReadAdvanceCustomStatus: boolean;
    usesBeneficiaryDocuments: boolean;
    canAccessMetadata: boolean;
}

export type TSettingsPage =
    | "account"
    | "period"
    | "notifications"
    | "statements"
    | "members"
    | "notifications"
    | "general"
    | "company"
    | "billing"
    | "history"
    | "api"
    | "channels"
    | "formatTypes"
    | "sales"
    | "analytical"
    | "accounting"
    | "brand"
    | "integrations"
    | "metadata"
    | "beneficiaryDocuments"
    | "terms"
    | "invoicePercentages"
    | "email"
    | "contracts"
    | "export"
    | "services"
    | "currency"
    | "partner-statements"
    | "edi-interforum"
    | "permissions"
    | "docoon"
    | "advance-provision"
    | "advance-statuses";

type TProps = {
    page: TSettingsPage | undefined;
};
export type TAccessCondition = {
    canAccessContractTemplates: boolean;
};
const Settings = ({ page }: TProps) => {
    const portalType = useContext(PortalType);
    const [t] = useTranslation();
    const settingsStore = useSelector((state: RootState) => state.settings);
    const [settings, setSettings] = useState<TSettings>(settingsStore);
    const { getCompanySettings } = useCompanySettings();
    const usesBrands = getCompanySettings("uses_multiple_brands");
    const usesAgreements = getCompanySettings("uses_agreements");
    const usesMultiCurrency = getCompanySettings("uses_multiple_currencies");
    const usesStatementTemplates = getCompanySettings("uses_custom_statement_templates");
    const hasBeneficiaryPortal = getCompanySettings("uses_portal");
    const usesMetadata = getCompanySettings("uses_metadata") ?? false;
    const usesIRCEC = getCompanySettings("uses_ircec");
    const usesScopedNotifications = getCompanySettings("uses_scoped_notifications");
    const usesInterforumEDI = getCompanySettings("uses_interforum_edi");
    const usesCustomRoles = getCompanySettings("uses_custom_roles") ?? false;
    const usesBeneficiaryDocuments = !!getCompanySettings("uses_beneficiary_documents_tracking");

    const navigate = useNavigate();
    const { pathname } = useLocation();
    usePageTitle(t("Settings.Settings"));

    const { userRole } = useUserRole();
    const { user } = useAuthenticator((context) => [context.user]);
    const accountType = getCognitoUserAttribute("custom:accountType", user);
    const { userData } = useAppSelector((state) => state.settings);
    const ability = useAbility();
    const canAccessTitleMetadata = ability.can("access", "CustomTitleSettings");
    const canAccessBeneficiaryMetadata = ability.can("access", "CustomBeneficiarySettings");
    const metadataResource = metadataResourceForAccess(
        metadataResourceFromPath(pathname),
        canAccessTitleMetadata,
        canAccessBeneficiaryMetadata,
    );

    const { hasBexioIntegration, hasDocoonIntegration } = useAppSelector((state) => state.integrations);

    const barExtraConditions: IExtraConditions = {
        usesBrands,
        usesAgreements,
        usesMultiCurrency,
        usesStatementTemplates: !!usesStatementTemplates,
        usesMetadata,
        usesBexio: hasBexioIntegration,
        usesIRCEC,
        usesScopedNotifications,
        usesInterforumEDI,
        usesCustomRoles,
        hasDocoonIntegration,
        canReadAdvanceProvision: ability.can("read", "AdvanceProvision"),
        canReadAdvanceCustomStatus: ability.can("read", "AdvanceCustomStatus"),
        usesBeneficiaryDocuments,
        canAccessMetadata: canAccessTitleMetadata || canAccessBeneficiaryMetadata,
    };

    // Map tab names to settings subjects for ability checks
    const tabToSettingsSubject: Record<string, string> = {
        brand: "BrandSettings",
        members: "MemberSettings",
        channels: "ChannelSettings",
        formatTypes: "ChannelSettings",
        sales: "ChannelSettings",
        services: "ServiceSettings",
        export: "ExportSettings",
        currency: "CurrencySettings",
        history: "HistorySettings",
        statements: "StatementSettings",
        "partner-statements": "PartnerStatementSettings",
        customTitle: "CustomTitleSettings",
        customBeneficiary: "CustomBeneficiarySettings",
        beneficiaryDocuments: "CustomBeneficiarySettings",
        terms: "TermSettings",
        email: "EmailSettings",
        accounting: "AccountingSettings",
        analytical: "AnalyticalSettings",
        notifications: "NotificationSettings",
        "edi-interforum": "IntegrationSettings",
        company: "CompanySettings",
        integrations: "IntegrationSettings",
        docoon: "IntegrationSettings",
        api: "ApiKeySettings",
        permissions: "PermissionSettings",
        contracts: "ContractSettings",
    };

    // Check access using ability-based permissions
    const hasPermission: Record<string, boolean> = Object.fromEntries(
        Object.entries(tabToSettingsSubject).map(([tabValue, subject]) => [tabValue, ability.can("access", subject)]),
    );

    // Determine the effective page based on permissions
    const canAccessCompanySettings = ability.can("access", "CompanySettings");
    const defaultPage: TSettingsPage = canAccessCompanySettings ? "company" : "account";
    const effectivePage: TSettingsPage = page ?? defaultPage;

    const showForDemo = userData?.type === "admin" && userData?.email?.includes("crealo.app");
    const showInvoicePercentagesPage = (portalType === EPortalType.BOOK && hasBexioIntegration) || showForDemo;

    return (
        <div className="publisher-settings-page">
            <NavigationBarSettings page={effectivePage} extraConditions={barExtraConditions} />
            <div className="settings-container">
                <TabContext value={effectivePage}>
                    {/**
                     * PROFILE SETTINGS + Notifications
                     */}
                    <TabPanel value="account">
                        <AccountSettings state={settings.accountSettings} setState={setSettings} />
                    </TabPanel>
                    {/**
                     * WORKSPACE SETTINGS
                     */}
                    <TabPanel value="company">
                        {portalType == EPortalType.URSSAF ? (
                            <URSSAFCompanySettings />
                        ) : (
                            <CompanyInfo portalType={portalType} />
                        )}
                    </TabPanel>
                    {usesBrands && hasPermission["brand"] && (
                        <TabPanel value="brand">
                            <MultipleBrandSettings />
                        </TabPanel>
                    )}
                    {hasPermission["members"] && (
                        <TabPanel value="members">
                            <MemberList />
                        </TabPanel>
                    )}
                    {usesScopedNotifications && hasPermission["notifications"] && (
                        <TabPanel value="notifications">
                            <ScopedNotificationSettings />
                        </TabPanel>
                    )}
                    {portalType !== EPortalType.URSSAF && hasPermission["channels"] && (
                        <TabPanel value="channels">
                            <Channels />
                        </TabPanel>
                    )}
                    {portalType === EPortalType.BOOK && hasPermission["formatTypes"] && (
                        <TabPanel value="formatTypes">
                            <FormatTypesSettings />
                        </TabPanel>
                    )}
                    {usesIRCEC && hasPermission["services"] && (
                        <TabPanel value="services">
                            <ServicesPage />
                        </TabPanel>
                    )}
                    {portalType === EPortalType.BOOK && hasPermission["sales"] && (
                        <TabPanel value="sales">
                            <CalculationBaseColumnLabels />
                        </TabPanel>
                    )}
                    {hasPermission["export"] && (
                        <TabPanel value="export">
                            <ExportSettings />
                        </TabPanel>
                    )}
                    {usesMultiCurrency && hasPermission["currency"] && (
                        <TabPanel value="currency">
                            <CurrencySettings />
                        </TabPanel>
                    )}
                    {hasPermission["history"] && (
                        <TabPanel value="history">
                            <History />
                        </TabPanel>
                    )}
                    {hasPermission["edi-interforum"] && usesInterforumEDI && (
                        <TabPanel value="edi-interforum">
                            <EDIInterforumSettings />
                        </TabPanel>
                    )}

                    {/**
                     * CUSTOMIZATION SETTINGS
                     */}
                    {portalType !== EPortalType.URSSAF && hasPermission["statements"] && (
                        <TabPanel value="statements">
                            <StatementSettings hasDataSettings={["audiovisual", "press"].includes(portalType)} />
                        </TabPanel>
                    )}
                    {usesStatementTemplates && hasPermission["partner-statements"] && (
                        <TabPanel value="partner-statements">
                            <PartnerStatementsSettings />
                        </TabPanel>
                    )}
                    {usesMetadata && (hasPermission["customTitle"] || hasPermission["customBeneficiary"]) && (
                        <TabPanel value="metadata" className="metadata-settings-panel">
                            <CustomFieldsContainer resource={metadataResource} hasPortal={hasBeneficiaryPortal} />
                        </TabPanel>
                    )}
                    {usesBeneficiaryDocuments && hasPermission["beneficiaryDocuments"] && (
                        <TabPanel value="beneficiaryDocuments">
                            <BeneficiaryDocumentsSettings />
                        </TabPanel>
                    )}
                    {hasPermission["terms"] && (
                        <TabPanel value="terms">
                            <TermTemplates />
                        </TabPanel>
                    )}

                    {hasPermission["email"] && (
                        <TabPanel value="email">
                            <AccountEmail />
                        </TabPanel>
                    )}
                    {hasPermission["accounting"] && (
                        <TabPanel value="accounting">
                            <AccountingSettings />
                        </TabPanel>
                    )}
                    {portalType === EPortalType.BOOK && barExtraConditions.canReadAdvanceProvision && (
                        <TabPanel value="advance-provision">
                            <AdvanceProvisionSettings />
                        </TabPanel>
                    )}
                    {portalType === EPortalType.BOOK && barExtraConditions.canReadAdvanceCustomStatus && (
                        <TabPanel value="advance-statuses">
                            <AdvanceCustomStatusSettings />
                        </TabPanel>
                    )}
                    {hasPermission["analytical"] && (
                        <TabPanel value="analytical">
                            <AnalyticalCode />
                        </TabPanel>
                    )}
                    {usesAgreements && hasPermission["contracts"] && (
                        <TabPanel value="contracts">
                            <ContractsSettings />
                        </TabPanel>
                    )}

                    {/**
                     * ADVANCED SETTINGS
                     */}
                    <TabPanel value="integrations">
                        <IntegrationsSettings />
                    </TabPanel>
                    {hasDocoonIntegration && hasPermission["docoon"] && (
                        <TabPanel value="docoon">
                            <DocoonSettings />
                        </TabPanel>
                    )}
                    {showInvoicePercentagesPage && (
                        <TabPanel value="invoicePercentages">
                            <InvoicePercentages />
                        </TabPanel>
                    )}
                    {hasPermission["api"] && (
                        <TabPanel value="api">
                            <APIKeysSettings />
                        </TabPanel>
                    )}
                    {usesCustomRoles && hasPermission["permissions"] && (
                        <TabPanel value="permissions">
                            <RolesSettings />
                        </TabPanel>
                    )}
                </TabContext>
            </div>
        </div>
    );
};

export default Settings;
